import React, { useState, useEffect } from 'react';
import { X, Heart, QrCode, CheckCircle, Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { tipService, DriverTip } from '../../services/tipService';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../../utils/priceFormat';

interface TipDriverModalProps {
  isOpen: boolean;
  onClose: () => void;
  deliveryId: string;
  driverId: string;
  driverName?: string;
  driverPromptPay?: string;
}

const TIP_AMOUNTS = [5, 10, 15, 25];

export default function TipDriverModal({
  isOpen,
  onClose,
  deliveryId,
  driverId,
  driverName,
  driverPromptPay
}: TipDriverModalProps) {
  const { t } = useTranslation();
  const [selectedAmount, setSelectedAmount] = useState<number>(TIP_AMOUNTS[0]);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentTip, setCurrentTip] = useState<DriverTip | null>(null);
  const [step, setStep] = useState<'select' | 'qr' | 'completed'>('select');

  const finalAmount = isCustom ? parseFloat(customAmount) || 0 : selectedAmount;

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setStep('select');
      setCurrentTip(null);
      setSelectedAmount(TIP_AMOUNTS[0]);
      setCustomAmount('');
      setIsCustom(false);
    }
  }, [isOpen]);

  const handleCreateTip = async () => {
    if (!driverPromptPay) {
      toast.error(t('tipDriver.errors.noPromptPay'));
      return;
    }

    if (finalAmount <= 0) {
      toast.error(t('tipDriver.errors.invalidAmount'));
      return;
    }

    setLoading(true);
    try {
      const tip = await tipService.createTip({
        delivery_id: deliveryId,
        driver_id: driverId,
        amount: finalAmount,
        promptpay_account: driverPromptPay
      });

      setCurrentTip(tip);
      setStep('qr');
    } catch (error) {
      console.error('Error creating tip:', error);
      toast.error(t('tipDriver.errors.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTip = async () => {
    if (!currentTip) return;

    setLoading(true);
    try {
      await tipService.completeTip(currentTip.id);
      setStep('completed');
      toast.success(t('tipDriver.success.completed'));
    } catch (error) {
      console.error('Error completing tip:', error);
      toast.error(t('tipDriver.errors.completeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCustomAmountChange = (value: string) => {
    // Allow only numbers and decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      setCustomAmount(value);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center">
            <Heart className="h-5 w-5 mr-2 text-red-500" />
            {t('tipDriver.title')}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {step === 'select' && (
            <div className="space-y-6">
              {/* Driver info */}
              <div className="text-center">
                <h3 className="font-medium text-lg">{t('tipDriver.tipFor')}</h3>
                <p className="text-gray-600">{driverName || t('tipDriver.yourDriver')}</p>
              </div>

              {/* Amount selection */}
              <div>
                <h4 className="font-medium mb-3">{t('tipDriver.selectAmount')}</h4>
                
                {/* Direct to driver notice */}
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700 text-center">
                    {t('tipDriver.directToDriver')}
                  </p>
                </div>
                
                {/* Preset amounts */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {TIP_AMOUNTS.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => {
                        setSelectedAmount(amount);
                        setIsCustom(false);
                      }}
                      className={`p-3 rounded-lg border-2 font-medium transition-colors ${
                        !isCustom && selectedAmount === amount
                          ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      ฿{amount}
                    </button>
                  ))}
                </div>

                {/* Custom amount */}
                <div>
                  <button
                    onClick={() => setIsCustom(true)}
                    className={`w-full p-3 rounded-lg border-2 font-medium transition-colors mb-2 ${
                      isCustom
                        ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {t('tipDriver.customAmount')}
                  </button>
                  
                  {isCustom && (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                        ฿
                      </span>
                      <input
                        type="text"
                        value={customAmount}
                        onChange={(e) => handleCustomAmountChange(e.target.value)}
                        placeholder="0.00"
                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                        autoFocus
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Confirm button */}
              <button
                onClick={handleCreateTip}
                disabled={loading || finalAmount <= 0}
                className="w-full py-3 bg-amber-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('tipDriver.confirmTip', { amount: formatPrice(finalAmount, 'thb') })
                )}
              </button>
            </div>
          )}

          {step === 'qr' && currentTip && (
            <div className="space-y-6 text-center">
              <div>
                <h3 className="font-medium text-lg mb-2">{t('tipDriver.scanToPay')}</h3>
                <p className="text-gray-600 mb-4">
                  {t('tipDriver.qrInstructions')}
                </p>
              </div>

              {/* QR Code */}
              <div className="flex justify-center">
                <div className="border border-gray-300 rounded-lg p-4 bg-white">
                  {/* Thai QR Payment header */}
                  <div 
                    className="bg-blue-800 text-white p-3 rounded-t-lg flex items-center justify-center mb-4"
                  >
                    <QrCode className="h-5 w-5 mr-2" />
                    <span className="font-bold">THAI QR PAYMENT</span>
                  </div>
                  
                  {currentTip.qr_code_data && (
                    <QRCodeSVG 
                      value={currentTip.qr_code_data} 
                      size={200}
                      level="H"
                      includeMargin={true}
                      className="rounded-md"
                    />
                  )}
                  
                  <div className="mt-4">
                    <div className="text-2xl font-bold text-amber-600">
                      ฿{formatPrice(currentTip.amount, 'thb')}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {t('tipDriver.tipFor')} {driverName || t('tipDriver.yourDriver')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Complete payment button */}
              <button
                onClick={handleCompleteTip}
                disabled={loading}
                className="w-full py-3 bg-green-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    {t('tipDriver.processing')}
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    {t('tipDriver.confirmPayment')}
                  </>
                )}
              </button>

              <p className="text-sm text-gray-500">
                {t('tipDriver.paymentNote')}
              </p>
            </div>
          )}

          {step === 'completed' && (
            <div className="space-y-6 text-center">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </div>
              
              <div>
                <h3 className="font-medium text-lg mb-2">{t('tipDriver.success.title')}</h3>
                <p className="text-gray-600">
                  {t('tipDriver.success.message', { 
                    amount: currentTip?.amount ? formatPrice(currentTip.amount, 'thb') : '0', 
                    driver: driverName || t('tipDriver.yourDriver')
                  })}
                </p>
              </div>

              <button
                onClick={onClose}
                className="w-full py-3 bg-amber-500 text-white rounded-lg font-medium"
              >
                {t('common.close')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 